import { tracker } from '../../../tracker';
import { PlaybackStartedEvent } from '../../../tracker/events/playback-started';
import { shouldCapBandwidthForThisStream } from '../../../utils/vodUtils';
import { queryClient } from '../../client';
import { AtsHttpClient } from '../ats-http-client';
import { fetchBookmarks } from '../me/bookmarks/api';
import { Bookmark } from '../me/bookmarks/types';
import { settings } from '../settings';
import { titleQuery } from '../titles/queries';
import { PlayablePlaybackOption } from '../titles/types/playback-option';
import { EpisodeTitle, MovieTitle, PlayableTitle, ProgramTitle, SeriesTitle } from '../titles/types/title';
import { playHuaweiProgram } from './adapters/play-huawei-program';
import { playHuaweiSvod } from './adapters/play-huawei-svod';
import { playHuaweiTrailer } from './adapters/play-huawei-trailer';
import { playHuaweiVod } from './adapters/play-huawei-vod';
import { findPlaybackOption } from './utils';
const atsHttpClient = new AtsHttpClient();

export interface PlayData {
  status: boolean;
  platform: 'cvp' | 'huawei';
  url: string;
  message: string;
  licenseUrl: string;
  token: string;
  sessionId: string;
  releasePid: string;
  account: string;
  mediaId: string;
  capBandwidthAt?: number;
}

export interface PlayOptions {
  platformTitleId: string;
  startSession: boolean;
  isTrailer: boolean;
  masterId?: string;
  providerIdentifier?: string;
}

export type PlayResponse = [PlayData | undefined, PlayableTitle, PlayablePlaybackOption, Bookmark | undefined];

export async function play(options: PlayOptions & { signal: AbortSignal | undefined }): Promise<PlayResponse> {
  // We don't pass a signal here because if it's aborted, an error will be thrown.
  // This means we will never reach `playHuawei`.
  let title: EpisodeTitle | MovieTitle | ProgramTitle | SeriesTitle;
  // Try catch for fetching title, this is needed in the case when a title is owned but no longer available in the "altibox universe"
  try {
    title = await queryClient.fetchQuery(titleQuery({ id: options.masterId ?? options.platformTitleId }));
  } catch (e) {
    console.error(e, ', Fetching title using platformTitleId');
    title = await queryClient.fetchQuery(titleQuery({ id: options.platformTitleId }));
  }

  if (title.type === 'series') {
    throw new Error('Series playback is not supported');
  }

  let playbackOption = findPlaybackOption(title, {
    platformTitleId: options.platformTitleId,
    providerIdentifier: options.providerIdentifier,
  });

  // retrying with platformtitle id
  if (playbackOption === undefined) {
    title = await queryClient.fetchQuery(titleQuery({ id: options.platformTitleId }));
    if (title.type === 'series') {
      throw new Error('Series playback is not supported');
    }
    playbackOption = findPlaybackOption(title, {
      platformTitleId: options.platformTitleId,
      providerIdentifier: options.providerIdentifier,
    });
  }

  if (playbackOption === undefined) {
    throw new Error('Playback option not found');
  }

  if (playbackOption.sourceId.toLowerCase().includes('huawei')) {
    const response =
      options.startSession || options.isTrailer
        ? await playHuawei(playbackOption, options.isTrailer, options.startSession)
        : undefined;

    if (options.isTrailer) {
      tracker.track(new PlaybackStartedEvent(title, playbackOption, 0, options.isTrailer));
      return [response, title, playbackOption, undefined];
    }

    if (response && shouldCapBandwidthForThisStream(playbackOption.distributor)) {
      console.log('Capping: 575');
      response.capBandwidthAt = 575;
    }
    try {
      const bookmarks = await fetchBookmarks();
      const bookmark = bookmarks.find((b) => b.platformTitleId === options.platformTitleId);
      tracker.track(new PlaybackStartedEvent(title, playbackOption, bookmark?.position ?? 0, options.isTrailer));
      return [response, title, playbackOption, bookmark];
    } catch {
      tracker.track(new PlaybackStartedEvent(title, playbackOption, 0, options.isTrailer));
      return [response, title, playbackOption, undefined];
    }
  }

  const response =
    options.startSession || options.isTrailer
      ? await atsHttpClient.post<PlayData>(
          `/playback/play/${options.platformTitleId}`,
          {
            providerIdentifier: playbackOption.providerIdentifier,
            trailer: options.isTrailer,
          },
          settings.playback('POST', options.signal),
        )
      : undefined;

  if (options.isTrailer) {
    tracker.track(new PlaybackStartedEvent(title, playbackOption, 0, options.isTrailer));
    return [response, title, playbackOption, undefined];
  }

  try {
    const bookmarks = await fetchBookmarks();
    const bookmark = bookmarks.find((b) => b.platformTitleId === options.platformTitleId);
    tracker.track(new PlaybackStartedEvent(title, playbackOption, bookmark?.position ?? 0, options.isTrailer));
    return [response, title, playbackOption, bookmark];
  } catch {
    tracker.track(new PlaybackStartedEvent(title, playbackOption, 0, options.isTrailer));
    return [response, title, playbackOption, undefined];
  }
}

async function playHuawei(
  option: PlayablePlaybackOption,
  isTrailer: boolean,
  shouldStartSession: boolean,
): Promise<PlayData> {
  if (isTrailer) {
    if (option.serviceType === 'program') {
      throw new Error('Trailers are not supported for programs');
    }

    return playHuaweiTrailer(option);
  }

  switch (option.serviceType) {
    case 'program':
      return playHuaweiProgram(option, shouldStartSession);
    case 'vod':
      return playHuaweiVod(option, shouldStartSession);
    case 'svod':
      return playHuaweiSvod(option, shouldStartSession);
    default:
      throw new Error('Unsupported service type');
  }
}

interface StartSessionOptions {
  duration: number;
  platformTitleId: string;
  providerIdentifier: string;
}

/**
 * Starts a playback session. This is often not needed as `/playback/play` will start a session automatically.
 * Used when we want to set a bookmark for an asset that is being played outside of ATS.
 */
export async function startSession(options: StartSessionOptions): Promise<string> {
  return atsHttpClient.post(`/playback/sessions`, options, settings.playback());
}

interface ComsumptionOptions {
  sessionId: string;
  position: number;
}

export async function reportConsumption({ sessionId, position }: ComsumptionOptions) {
  return atsHttpClient.post(
    `/playback/sessions/${sessionId}/consumption`,
    { position: Math.round(position) },
    settings.playback(),
  );
}

export async function stopConsumption({ sessionId, position }: ComsumptionOptions) {
  return atsHttpClient.delete(
    `/playback/sessions/${sessionId}`,
    { position: Math.round(position) },
    settings.playback(),
  );
}
